import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Field, Input, PasswordInput } from "@/components/base/FormControls";
import Alert from "@/components/base/Alert";
import { BRAND } from "@/config/brand";
import { useSession } from "@/session/SessionProvider";
import { homePathForRole, type AccountRole } from "@/session/types";
import { supabase } from "@/store/shared/supabase";
import { cn } from "@/utils/cn";

type AccountType = "proprietaire" | "agence" | null;

interface FormState {
  prenom: string;
  nom: string;
  agence: string;
  email: string;
  telephone: string;
  password: string;
  confirm: string;
  cgu: boolean;
}

const initialForm: FormState = {
  prenom: "",
  nom: "",
  agence: "",
  email: "",
  telephone: "",
  password: "",
  confirm: "",
  cgu: false,
};

function messageErreurInscription(message: string): string {
  const m = message.toLowerCase();
  if (m.includes("user already registered")) {
    return "Un compte existe déjà avec cette adresse e-mail. Connectez-vous plutôt.";
  }
  if (m.includes("password should be at least")) {
    return "Le mot de passe est trop court. Utilisez au moins 6 caractères.";
  }
  // Limite d'envoi des e-mails d'authentification : le service d'e-mail Supabase
  // est plafonné (≈2 e-mails/heure). Chaque inscription en envoie un (confirmation),
  // donc quelques inscriptions rapprochées suffisent à l'atteindre. On l'explique
  // clairement au lieu du générique « trop de tentatives ».
  if (m.includes("email rate limit") || m.includes("over_email_send_rate_limit")) {
    return "Aucun nouvel e-mail de confirmation ne peut être envoyé pour le moment : la limite d'envoi d'e-mails vient d'être atteinte. Patientez environ une heure, puis vérifiez d'abord votre boîte de réception et les spams avant de réessayer.";
  }
  // Limite de requêtes (trop d'appels en peu de temps) : relevant d'une protection
  // anti-abus, à conserver telle quelle.
  if (m.includes("rate limit") || m.includes("too many") || m.includes("over_request_rate_limit")) {
    return "Trop de tentatives en peu de temps. Patientez quelques minutes avant de réessayer.";
  }
  return "Création du compte impossible pour le moment. Vérifiez vos informations et réessayez.";
}

/** Bandeau de l'offre de lancement : 6 mois offerts, accès complet. */
function OffreLancementCard({ compact = false }: { compact?: boolean }) {
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-lg border border-secondary-200 bg-secondary-50",
        compact ? "px-4 py-3" : "p-4 md:p-5"
      )}
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
        <i className="ri-gift-2-line text-lg" aria-hidden="true" />
      </span>
      <div>
        <p className="text-sm font-label font-semibold text-secondary-900">
          Offre de lancement : 6 mois offerts, accès complet
        </p>
        <p className="mt-1 text-xs leading-relaxed text-secondary-900">
          Tous les nouveaux propriétaires et toutes les nouvelles agences bénéficient de
          <strong> 6 mois gratuits</strong> avec l'accès à l'ensemble des modules, sans aucune
          limite et sans paiement à l'inscription.
        </p>
      </div>
    </div>
  );
}

export default function SignupPage() {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const [type, setType] = useState<AccountType>(null);
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<null | "email">(null);

  const baseComplet =
    (type !== "agence" || form.agence.trim().length > 0) &&
    form.prenom.trim().length > 0 &&
    form.nom.trim().length > 0 &&
    /^\S+@\S+\.\S+$/.test(form.email) &&
    form.password.length >= 6 &&
    form.confirm === form.password &&
    form.cgu;

  const update = (key: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (type === "agence" && !form.agence.trim()) next.agence = "Le nom de l'agence est requis.";
    if (!form.prenom.trim()) next.prenom = "Le prénom est requis.";
    if (!form.nom.trim()) next.nom = "Le nom est requis.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = "Adresse e-mail invalide.";
    if (form.telephone && !/^[0-9+\s]{8,}$/.test(form.telephone)) next.telephone = "Numéro de téléphone invalide.";
    if (form.password.length < 6) next.password = "6 caractères minimum.";
    if (form.confirm !== form.password) next.confirm = "Les mots de passe ne correspondent pas.";
    if (!form.cgu) next.cgu = "Vous devez accepter les CGU.";
    return next;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Garde anti double-soumission : évite tout envoi multiple accidentel
    // (double-clic, double appui clavier) qui consommerait des e-mails de confirmation.
    if (submitting) return;
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const isAgence = type === "agence";
    const role: AccountRole = isAgence ? "dg_agence" : "dg_proprietaire";
    setSubmitting(true);
    setFormError("");

    // Aucune formule payante ni paiement à l'inscription : l'essai gratuit de
    // 6 mois est octroyé automatiquement côté serveur au premier accès. Le
    // navigateur n'envoie ni statut, ni formule, ni date (jamais de confiance).
    const { data, error } = await supabase.auth.signUp({
      email: form.email.trim(),
      password: form.password,
      options: {
        data: {
          role,
          nom_complet: `${form.prenom.trim()} ${form.nom.trim()}`.trim(),
          telephone: form.telephone.trim(),
        },
      },
    });

    if (error) {
      setSubmitting(false);
      setFormError(messageErreurInscription(error.message));
      return;
    }

    // Confirmation e-mail requise : aucune session n'est renvoyée. L'essai sera
    // attribué automatiquement à la première connexion (e-mail confirmé).
    if (!data.session) {
      setSubmitting(false);
      setResult("email");
      return;
    }

    // Session immédiate (confirmation e-mail désactivée) : on ajuste le nom de
    // l'agence puis on recharge le profil — l'essai est octroyé automatiquement.
    if (isAgence && form.agence.trim()) {
      try {
        const { data: profil } = await supabase.rpc("mon_profil");
        const agenceId = (profil as { agence?: { id?: string } } | null)?.agence?.id;
        if (agenceId) {
          await supabase.from("agences").update({ nom: form.agence.trim() }).eq("id", agenceId);
        }
      } catch {
        /* non bloquant */
      }
    }

    await refresh();
    setSubmitting(false);
    navigate(homePathForRole(role), { replace: true });
  };

  if (result) {
    return (
      <section className="px-4 py-14 md:py-20 bg-background-50">
        <div className="container-page max-w-xl animate-fade-up text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
            <i className="ri-mail-check-line text-2xl" aria-hidden="true" />
          </span>
          <h1 className="mt-6 text-2xl md:text-3xl font-heading font-semibold text-foreground-950">
            Vérifiez votre boîte e-mail
          </h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600">
            Un lien de confirmation a été envoyé à <strong>{form.email.trim()}</strong>. Cliquez dessus pour activer
            votre compte.
          </p>

          <div className="mt-6 text-left">
            <OffreLancementCard />
          </div>

          <div className="mx-auto mt-6 flex max-w-md items-center gap-3 rounded-md border border-background-200 bg-background-100 px-4 py-3 text-left">
            <i className="ri-information-line text-base text-foreground-500" aria-hidden="true" />
            <p className="text-xs text-foreground-600">
              {type === "agence"
                ? "Vos 6 mois gratuits démarrent automatiquement à votre première connexion, sans aucune autre étape."
                : "Après confirmation de votre e-mail, connectez-vous : vos 6 mois gratuits s'activent automatiquement à votre première connexion, sans aucune validation manuelle."}
            </p>
          </div>
          <Link
            to="/connexion"
            className="mt-8 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label font-medium text-background-50 hover:bg-primary-600 hover:-translate-y-0.5 transition-all cursor-pointer"
          >
            <i className="ri-login-box-line" aria-hidden="true" />
            Aller à la connexion
          </Link>
        </div>
      </section>
    );
  }

  if (!type) {
    return (
      <section className="px-4 py-14 md:py-20 bg-background-50">
        <div className="container-page max-w-3xl animate-fade-up">
          <div className="text-center">
            <h1 className="text-2xl md:text-4xl font-heading font-semibold text-foreground-950">
              Choisissez votre type de compte
            </h1>
            <p className="mx-auto mt-3 max-w-xl text-sm md:text-base text-foreground-600">
              Votre espace s'adapte à votre activité. Vous pourrez ajuster vos informations plus tard.
            </p>
          </div>

          <div className="mx-auto mt-8 max-w-2xl">
            <OffreLancementCard />
          </div>

          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setType("proprietaire")}
              className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-6 text-left transition-all hover:border-primary-400 hover:bg-primary-50/40 hover:-translate-y-1 cursor-pointer"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary-100 text-primary-700 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                <i className="ri-user-star-line text-2xl" aria-hidden="true" />
              </span>
              <h2 className="mt-4 text-lg font-heading font-semibold text-foreground-950">Propriétaire</h2>
              <p className="mt-2 text-sm text-foreground-600">
                Gérez vos propres immeubles, biens, locataires, contrats et loyers.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-label font-medium text-primary-700">
                Choisir Propriétaire <i className="ri-arrow-right-line" aria-hidden="true" />
              </span>
            </button>

            <button
              type="button"
              onClick={() => setType("agence")}
              className="group flex flex-col rounded-lg border border-background-200 bg-background-50 p-6 text-left transition-all hover:border-secondary-400 hover:bg-secondary-50/40 hover:-translate-y-1 cursor-pointer"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 group-hover:bg-secondary-500 group-hover:text-background-50 transition-colors">
                <i className="ri-building-4-line text-2xl" aria-hidden="true" />
              </span>
              <h2 className="mt-4 text-lg font-heading font-semibold text-foreground-950">Agence immobilière</h2>
              <p className="mt-2 text-sm text-foreground-600">
                Gérez plusieurs propriétaires, immeubles, employés et autorisations.
              </p>
              <span className="mt-5 inline-flex items-center gap-2 text-sm font-label font-medium text-secondary-700">
                Choisir Agence <i className="ri-arrow-right-line" aria-hidden="true" />
              </span>
            </button>
          </div>

          <p className="mt-8 text-center text-sm text-foreground-600">
            Déjà inscrit ?{" "}
            <Link to="/connexion" className="font-label font-medium text-primary-700 hover:text-primary-800">
              Se connecter
            </Link>
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 py-14 md:py-16 bg-background-50">
      <div className="container-page max-w-2xl animate-fade-up">
        <button
          type="button"
          onClick={() => setType(null)}
          className="inline-flex items-center gap-2 text-sm font-label font-medium text-foreground-600 hover:text-foreground-900 cursor-pointer"
        >
          <i className="ri-arrow-left-line" aria-hidden="true" />
          Changer de type de compte
        </button>

        <div className="mt-6 flex items-center gap-3">
          <span
            className={cn(
              "flex h-11 w-11 items-center justify-center rounded-lg",
              type === "agence" ? "bg-secondary-100 text-secondary-700" : "bg-primary-100 text-primary-700"
            )}
          >
            <i
              className={type === "agence" ? "ri-building-4-line text-xl" : "ri-user-star-line text-xl"}
              aria-hidden="true"
            />
          </span>
          <div>
            <h1 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950">
              {type === "agence" ? "Créer un compte Agence" : "Créer un compte Propriétaire"}
            </h1>
            <p className="text-sm text-foreground-600">{BRAND.tagline}</p>
          </div>
        </div>

        <div className="mt-6">
          <OffreLancementCard compact />
        </div>

        <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
          {formError && (
            <Alert tone="error" title="Inscription impossible">
              {formError}
            </Alert>
          )}
          {type === "agence" && (
            <Field label="Nom de l'agence" htmlFor="su-agence" error={errors.agence} required>
              <Input
                id="su-agence"
                name="agence"
                icon="ri-building-4-line"
                placeholder="Ex. Immobilière du Faso"
                value={form.agence}
                invalid={Boolean(errors.agence)}
                onChange={(e) => update("agence", e.target.value)}
              />
            </Field>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label={type === "agence" ? "Prénom du responsable" : "Prénom"}
              htmlFor="su-prenom"
              error={errors.prenom}
              required
            >
              <Input
                id="su-prenom"
                name="prenom"
                icon="ri-user-line"
                placeholder="Prénom"
                value={form.prenom}
                invalid={Boolean(errors.prenom)}
                onChange={(e) => update("prenom", e.target.value)}
              />
            </Field>
            <Field label={type === "agence" ? "Nom du responsable" : "Nom"} htmlFor="su-nom" error={errors.nom} required>
              <Input
                id="su-nom"
                name="nom"
                icon="ri-user-line"
                placeholder="Nom"
                value={form.nom}
                invalid={Boolean(errors.nom)}
                onChange={(e) => update("nom", e.target.value)}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="E-mail" htmlFor="su-email" error={errors.email} required>
              <Input
                id="su-email"
                name="email"
                type="email"
                autoComplete="email"
                icon="ri-mail-line"
                placeholder="vous@exemple.bf"
                value={form.email}
                invalid={Boolean(errors.email)}
                onChange={(e) => update("email", e.target.value)}
              />
            </Field>
            <Field label="Téléphone" htmlFor="su-tel" error={errors.telephone}>
              <Input
                id="su-tel"
                name="telephone"
                icon="ri-phone-line"
                placeholder="+226 ..."
                value={form.telephone}
                invalid={Boolean(errors.telephone)}
                onChange={(e) => update("telephone", e.target.value)}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Mot de passe" htmlFor="su-password" error={errors.password} required>
              <PasswordInput
                id="su-password"
                name="password"
                autoComplete="new-password"
                placeholder="6 caractères minimum"
                value={form.password}
                invalid={Boolean(errors.password)}
                onChange={(e) => update("password", e.target.value)}
              />
            </Field>
            <Field label="Confirmation" htmlFor="su-confirm" error={errors.confirm} required>
              <PasswordInput
                id="su-confirm"
                name="confirm"
                autoComplete="new-password"
                placeholder="Confirmer le mot de passe"
                value={form.confirm}
                invalid={Boolean(errors.confirm)}
                onChange={(e) => update("confirm", e.target.value)}
              />
            </Field>
          </div>

          <div className="flex items-start gap-2.5 rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-xs leading-relaxed text-foreground-600">
            <i className="ri-shield-check-line mt-0.5 text-sm text-secondary-600" aria-hidden="true" />
            <>
              Aucun paiement n'est demandé à l'inscription. Après confirmation de votre e-mail, vos{" "}
              <strong>6 mois gratuits</strong> avec accès complet s'activent automatiquement à votre première
              connexion — sans aucune validation manuelle.
            </>
          </div>

          <div>
            <label className="flex items-start gap-2.5 text-sm text-foreground-700">
              <input
                type="checkbox"
                name="cgu"
                className="mt-0.5 h-4 w-4 rounded border-background-300"
                checked={form.cgu}
                onChange={(e) => update("cgu", e.target.checked)}
              />
              <span>
                J'accepte les{" "}
                <Link to="/cgu" className="font-label font-medium text-primary-700 hover:text-primary-800">
                  conditions générales d'utilisation
                </Link>{" "}
                et la{" "}
                <Link to="/confidentialite" className="font-label font-medium text-primary-700 hover:text-primary-800">
                  politique de confidentialité
                </Link>
                .
              </span>
            </label>
            {errors.cgu && (
              <p className="mt-1.5 flex items-center gap-1 text-xs text-primary-700">
                <i className="ri-error-warning-line text-[13px]" aria-hidden="true" />
                {errors.cgu}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || !baseComplet}
            className="mt-2 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-label font-medium text-background-50 hover:bg-primary-600 hover:-translate-y-0.5 transition-all cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-background-50/40 border-t-background-50" />
            )}
            {submitting ? "Création…" : "Créer mon compte — 6 mois offerts"}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-foreground-600">
          Déjà inscrit ?{" "}
          <Link to="/connexion" className="font-label font-medium text-primary-700 hover:text-primary-800">
            Se connecter
          </Link>
        </p>
      </div>
    </section>
  );
}